import { Mail, Phone, MapPin } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { COMPANY } from "@/data/content";

const LAST_UPDATED = "September 21, 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-10">
      <h2 className="font-display text-xl sm:text-2xl font-bold text-white mb-3">{title}</h2>
      <div className="text-slate-400 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

// Meta's WhatsApp Business Platform app review checks that a submitting
// business has a real, substantive Terms of Service — not a placeholder or a
// page that just points somewhere else — and specifically looks for a clear
// description of what WhatsApp messaging is used for, how a user opts in,
// and how they can opt out. Section 4 below exists for exactly that reason.
export default function Terms() {
  const { config } = useSite();
  const val = (k, fallback) => (config && config[k]) || fallback;
  const email = val("email", "[ENTER ACTUAL EMAIL]");
  const phone = val("phone", "[ENTER ACTUAL PHONE NUMBER]");
  const address = val("address", "[ENTER ACTUAL ADDRESS]");
  const brandName = config?.brand || COMPANY.name;
  const domain = config?.domain || COMPANY.domain;

  useSeo({
    title: `Terms & Conditions | ${brandName}`,
    description: `The terms and conditions governing use of the ${brandName} website and communications, including WhatsApp messaging.`,
    canonical: `https://${domain}/terms`,
  });

  return (
    <div>
      <PageHeader
        label="Legal"
        title="Terms & Conditions"
        sub={`Last updated: ${LAST_UPDATED}. These terms govern your use of the ${brandName} website and any communications you receive from us, including over WhatsApp.`}
      />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Section id="acceptance" title="1. Acceptance of These Terms">
          <p>
            These Terms & Conditions ("Terms") govern your access to and use of the {brandName} website ({domain})
            (the "Site") and any communications you receive from {brandName} ("we", "us", "our"), including messages
            sent over WhatsApp. By using this Site, submitting a form on it, or opting in to receive messages from
            us, you agree to these Terms. If you do not agree, please do not use the Site or opt in to our
            communications.
          </p>
        </Section>

        <Section id="description" title="2. Description of Service">
          <p>
            {brandName} is a solar, energy storage and power solutions company. Through this Site we provide
            information about our products and services (solar inverters, hybrid inverters, UPS systems, LFP
            batteries, energy storage systems, EV charging solutions, Solar BOS and EPC services), and allow visitors
            to submit enquiries or requests for a quote through our "Contact Us" and "Get a Quote" forms.
          </p>
        </Section>

        <Section id="accounts" title="3. Accounts and the Admin Area">
          <p>
            The Site includes a password-protected admin area used only by authorised {brandName} personnel to
            manage enquiries and site content. It is not available for public registration. Anyone with admin
            access is responsible for keeping their login credentials confidential and for all activity under their
            account.
          </p>
        </Section>

        <Section id="whatsapp" title="4. WhatsApp and Other Messaging Communications">
          <p>
            When you submit an enquiry through this Site, provide your phone number to us, or otherwise opt in
            (for example, by messaging us first on WhatsApp or clicking a "Chat on WhatsApp" link), you consent to
            {" "}{brandName} contacting you over WhatsApp, SMS, phone call or email regarding that enquiry —
            including providing product information, quotes, order/service updates, and answering your questions.
          </p>
          <p>
            <strong>What you can expect:</strong> message frequency is based on your enquiry and any ongoing
            conversation with our team — we do not send recurring automated marketing broadcasts unless you have
            separately agreed to receive them. Standard message and data rates from your carrier may apply.
          </p>
          <p>
            <strong>How to opt out:</strong> you can stop receiving WhatsApp messages from us at any time by
            replying "STOP" in the chat, blocking our WhatsApp business number, or telling us directly that you no
            longer wish to be contacted. You can also request this by email or phone using the details in Section 12.
            We will honour opt-out requests promptly.
          </p>
          <p>
            Any personal data we receive through WhatsApp or other messaging channels is handled as described in our{" "}
            <a href="/privacy-policy" className="text-emerald-400 hover:underline">Privacy Policy</a>, and you can
            request its deletion at any time — see our{" "}
            <a href="/data-deletion" className="text-emerald-400 hover:underline">Data Deletion</a> page.
          </p>
        </Section>

        <Section id="acceptable-use" title="5. Acceptable Use">
          <p>You agree not to use the Site or our messaging channels to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>submit false, misleading, or fraudulent information through our forms;</li>
            <li>attempt to gain unauthorised access to the admin area or any non-public part of the Site;</li>
            <li>interfere with or disrupt the Site's operation, security, or availability; or</li>
            <li>use automated means (bots, scrapers) to extract content from the Site without our permission.</li>
          </ul>
        </Section>

        <Section id="ip" title="6. Intellectual Property">
          <p>
            All content on this Site — text, images, logos, product names and designs — is owned by {brandName} or
            used with permission, and is protected by applicable intellectual property laws. You may view and share
            pages of this Site for personal, non-commercial reference, but you may not copy, reproduce, or
            redistribute our content for commercial purposes without our prior written consent.
          </p>
        </Section>

        <Section id="third-party" title="7. Third-Party Services and Links">
          <p>
            This Site and our communications may reference or link to third-party services we rely on to operate —
            including WhatsApp/Meta Platforms, our hosting provider, and our database provider (see our{" "}
            <a href="/privacy-policy" className="text-emerald-400 hover:underline">Privacy Policy</a> Section 9 for
            the full list) — and may occasionally link to third-party websites (for example, our social media
            pages). We do not control third-party sites or services, and use of them is subject to their own terms
            and privacy policies, not ours.
          </p>
        </Section>

        <Section id="disclaimer" title="8. Disclaimer of Warranties">
          <p>
            This Site and the information on it are provided "as is" and "as available", without warranties of any
            kind, express or implied. While we make reasonable efforts to keep product and pricing information
            accurate and up to date, we do not guarantee that all content on the Site is complete, current, or
            error-free. Final product specifications, pricing, and availability are confirmed directly with our
            team at the time of your enquiry or order.
          </p>
        </Section>

        <Section id="liability" title="9. Limitation of Liability">
          <p>
            To the fullest extent permitted by applicable law, {brandName} shall not be liable for any indirect,
            incidental, or consequential damages arising from your use of the Site or our communications, including
            WhatsApp messages. Nothing in these Terms limits any liability that cannot be limited under applicable
            Indian law.
          </p>
        </Section>

        <Section id="indemnification" title="10. Indemnification">
          <p>
            You agree to indemnify and hold {brandName} harmless from any claims, losses, or damages arising from
            your misuse of the Site, your violation of these Terms, or information you submit to us that infringes
            the rights of, or causes harm to, a third party.
          </p>
        </Section>

        <Section id="governing-law" title="11. Governing Law">
          <p>
            These Terms are governed by the laws of India. Any dispute arising from these Terms or your use of the
            Site shall be subject to the exclusive jurisdiction of the courts having jurisdiction over our
            registered place of business.
          </p>
        </Section>

        <Section id="changes" title="12. Changes to These Terms">
          <p>
            We may update these Terms from time to time to reflect changes in our practices, services, or legal
            requirements. Any changes will be posted on this page with a revised "Last updated" date. Your continued
            use of the Site or continued opt-in to our messaging after a change means you accept the updated Terms.
          </p>
        </Section>

        <Section id="contact" title="13. Contact Information">
          <p>If you have any questions about these Terms, or wish to opt out of WhatsApp or other messaging, please contact us:</p>
          <div className="card-surface p-6 mt-2 space-y-3 text-sm not-italic">
            <div className="text-white font-semibold">{brandName}</div>
            <div className="flex items-start gap-3"><MapPin className="text-emerald-400 shrink-0" size={18} /><span>{address}</span></div>
            <div className="flex items-start gap-3"><Phone className="text-emerald-400 shrink-0" size={18} /><span>{phone}</span></div>
            <div className="flex items-start gap-3"><Mail className="text-emerald-400 shrink-0" size={18} /><span>{email}</span></div>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            See also our <a href="/privacy-policy" className="text-emerald-400 hover:underline">Privacy Policy</a> and{" "}
            <a href="/data-deletion" className="text-emerald-400 hover:underline">Data Deletion</a> pages.
          </p>
        </Section>
      </section>
    </div>
  );
}
